import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle } from 'lucide-react';
import { setJsonLd, setPageSeo } from '../utils/seo';
import './AboutPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AboutPage() {
  const [nlEmail, setNlEmail] = useState('');
  const [nlName, setNlName] = useState('');
  const [nlStatus, setNlStatus] = useState(null); // 'success' | 'error' | 'already' | null
  const [nlLoading, setNlLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!nlEmail) return;
    setNlLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/leads/optin`, {
        email: nlEmail,
        name: nlName,
        source: 'about-newsletter',
      });
      setNlStatus(res.data.status === 'already_subscribed' ? 'already' : 'success');
    } catch (err) {
      setNlStatus('error');
    } finally {
      setNlLoading(false);
    }
  };
  useEffect(() => {
    setPageSeo({
      title: 'About Us | Binding Thread \u2014 Keepsake Letters',
      description: 'Founded by Mary-Ann Roche after losing her parents\u2019 untold stories, Binding Thread gives your loved one a free number to call and share memories. We turn their words into beautifully crafted keepsake letter collections \u2014 no technology needed.',
      canonical: 'https://www.bindingthread.com.au/about',
      ogImage: 'https://www.bindingthread.com.au/logo-final.png'
    });
    setJsonLd('ld-about', {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "mainEntity": {
        "@type": "Organization",
        "name": "Binding Thread",
        "url": "https://www.bindingthread.com.au",
        "founder": {
          "@type": "Person",
          "name": "Mary-Ann Roche",
          "jobTitle": "Founder",
          "description": "Mary-Ann Roche founded Binding Thread to give families a simple, screen-free way to preserve their loved one\u2019s spoken memories as keepsake letters."
        },
        "description": "Binding Thread is a keepsake memory service that transforms phone call recordings into beautifully crafted keepsake letters on archival-quality paper."
      }
    });
  }, []);

  return (
    <div className="about-page" data-testid="about-page">
      <section className="about-hero">
        <img
          className="about-hero-bg"
          src="https://images.unsplash.com/photo-1614991539310-630818071643?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHx2aW50YWdlJTIwbGV0dGVycyUyMGVudmVsb3BlcyUyMGZsb3dlcnMlMjBzb2Z0JTIwd2FybSUyMG92ZXJoZWFkfGVufDB8fHx8MTc3NzExNTQ0MXww&ixlib=rb-4.1.0&q=80&w=1600"
          alt=""
          aria-hidden="true"
        />
        <div className="container">
          <h1>About Binding Thread</h1>
          <p className="about-hero-sub">The Family I Thought I Knew</p>
        </div>
      </section>

      <section className="about-content">
        <div className="container">
          <div className="content-wrapper">

            {/* Opening (no heading — flows from hero sub-heading) */}
            <div className="about-section">
              <p>
                I grew up as one of ten children on a dairy farm and as you can imagine there was never a quiet moment. Never an empty seat at the table. Never much time for anything that wasn&apos;t the farm, the cows, or football.
              </p>
              <p>
                My parents worked so hard. They carried all the responsibility without complaint and kept moving because there was always something that needed doing.
              </p>
              <p>
                My mother, especially, spent her life taking care of everyone else. Not because she was distant, but because there simply wasn&apos;t space for her to be anything other than needed.
              </p>
              <p>
                When my parents passed away, I realised something that stayed with me.
              </p>
              <p>
                I didn&apos;t really know them as people as well as I thought I did.
              </p>
              <p>
                I knew the parent version of them.<br />
                The hardworking version.<br />
                The tired version.
              </p>
              <p>
                But I never really knew:
              </p>
              <ul className="about-list">
                <li>who they were before children</li>
                <li>what they dreamed about</li>
                <li>what they regretted</li>
                <li>what they were proud of</li>
                <li>what they would have done differently</li>
              </ul>
              <p>
                There was never the time to ask.
              </p>
              <p>
                And then one day, there was no more time at all.
              </p>
              <p>
                My mother remarried in her 60s, and I know almost nothing about that chapter of her life. That still affects me.
              </p>
            </div>

            <div className="section-divider"></div>

            {/* The Stories We Never Think To Save */}
            <div className="about-section">
              <h2>The Stories We Never Think To Save</h2>
              <p>
                One of my siblings eventually created a documentary about our family. Watching it brought back memories I didn&apos;t even realise I&apos;d forgotten.
              </p>
              <p>
                Small things.<br />
                Voices.<br />
                Moments.<br />
                Stories.
              </p>
              <p>
                It reminded me how powerful it is to hear someone&apos;s memories in their own words.
              </p>
              <p>
                But it also made me realise something else.
              </p>
              <p>
                Most families never get that opportunity.
              </p>
              <p>
                Most families don&apos;t have someone who thinks to preserve those stories before it&apos;s too late.
              </p>
              <p>
                And even when they do want to preserve them, life gets in the way.
              </p>
              <p>
                People are busy.<br />
                Parents get older.<br />
                Health changes.<br />
                Time passes quietly.
              </p>
              <p>
                Then suddenly the stories are gone with the person who carried them.
              </p>
            </div>

            <div className="section-divider"></div>

            {/* Why I Built Binding Thread */}
            <div className="about-section">
              <h2>Why I Built Binding Thread</h2>
              <p>
                Binding Thread was never created as just a product, but a solution to something I lived through personally.
              </p>
              <p>
                I kept thinking about how difficult memory preservation often becomes for older people or people who are unwell.
              </p>
              <p>
                Most solutions rely on:
              </p>
              <ul className="about-list">
                <li>apps</li>
                <li>recordings</li>
                <li>cameras</li>
                <li>uploads</li>
                <li>technology</li>
              </ul>
              <p>
                But many people simply don&apos;t want that.
              </p>
              <p>
                I remembered calling my own mother in the nursing home every day. As she became older and frailer, she would often rest the phone on her shoulder while we talked.
              </p>
              <p>
                That stayed with me.
              </p>
              <p>
                Because even when everything else becomes difficult, conversation still feels natural and seems to flow.
              </p>
              <p>
                So I built Binding Thread around the simplest thing possible:
              </p>
              <p className="highlight-text"><strong>A phone call.</strong></p>
              <p>
                Your loved one simply calls our free number whenever a memory comes to mind. They talk naturally, in their own time, in their own words. We don&apos;t care if it&apos;s 1am or 1pm, anytime is fine. Memories come and go so structuring a suitable time to record them makes no sense to me.
              </p>
              <p>
                We carefully transform those memories into beautifully crafted keepsake letters and send them to you.
              </p>
              <p>
                No apps.<br />
                No technology.<br />
                No learning curve.
              </p>
              <p>
                Just stories preserved before they disappear.
              </p>
            </div>

            <div className="section-divider"></div>

            {/* Something Unexpected Happened */}
            <div className="about-section">
              <h2>Something Unexpected Happened</h2>
              <p>
                Somewhere along the way, building Binding Thread changed the way I thought about my own life too.
              </p>
              <p>
                I started thinking about my own children.
              </p>
              <p>
                My adult children who are now busy building lives of their own.
              </p>
              <p>
                And I found myself wondering:
              </p>
              <p className="highlight-text"><strong>How well do they really know me?</strong></p>
              <p>
                Not the mum version of me. Not the practical version who showed up and sorted things out.
              </p>
              <p>
                But me.
              </p>
              <p>
                The stories from before they were born. The things I struggled through. The things I believe in. The memories I&apos;ve never quite found the right moment to talk about.
              </p>
              <p>
                Building Binding Thread made me realise that one day, I want to leave my children more than possessions.
              </p>
              <p>
                I want to leave them stories.
              </p>
              <p>
                The kind they can hold in their hands years from now and still hear my voice inside.
              </p>
              <p className="promise-text"><strong>That&apos;s what Binding Thread is really about.</strong></p>
            </div>

            <div className="section-divider"></div>

            {/* A Personal Promise — Founder */}
            <div className="about-section founder-section">
              <h2>A Personal Promise</h2>
              <div className="founder-content">
                <div className="founder-image">
                  <img
                    src="https://customer-assets.emergentagent.com/job_ab110b33-d84b-4603-b690-422196896d5d/artifacts/8tb1827x_7aeb9300-bff9-4a3c-83a9-0bf19a2c28f9.png"
                    alt="Mary-Ann Roche, founder of Binding Thread"
                    width="400"
                    height="400"
                  />
                </div>
                <div className="founder-text">
                  <p>
                    Binding Thread is based on the Gold Coast, and every letter is personally overseen by me.
                  </p>
                  <p>
                    I read what your loved one shares. I make sure every story is handled with care, respect and sensitivity.
                  </p>
                  <p>
                    Because I know firsthand what it feels like to almost lose those memories forever.
                  </p>
                  <p>
                    And I also know how meaningful it is to get even part of them back. If someone you love still has stories to tell, there is still time.
                  </p>
                  <p>
                    Don&apos;t wait for the day you wish you had started.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="about-cta">
              <Link to="/shop" className="btn btn-urgent" data-testid="about-cta-btn">
                Start Capturing Their Stories
              </Link>
              <div className="about-cross-links">
                <p className="founder-sign-off">&mdash; Mary-Ann Roche, Founder, Binding Thread</p>
              </div>
            </div>

            {/* Newsletter signup — "Stay in touch with Mary-Ann" */}
            <section className="about-newsletter" data-testid="about-newsletter">
              <div className="about-newsletter-inner">
                <h2 className="about-newsletter-title">Stay in touch with Mary-Ann</h2>
                <p className="about-newsletter-sub">
                  Quiet updates from the studio &mdash; stories families have shared, gentle reminders to call the ones you love, and occasional ideas for preserving their voice. Never spammy. Unsubscribe anytime.
                </p>

                {nlStatus === 'success' || nlStatus === 'already' ? (
                  <div className="about-newsletter-success" data-testid="about-newsletter-success">
                    <CheckCircle size={22} />
                    <p>
                      {nlStatus === 'already'
                        ? "You're already on the list \u2014 thank you."
                        : "Thank you. You'll hear from me soon."}
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubscribe} className="about-newsletter-form" data-testid="about-newsletter-form">
                    <input
                      type="text"
                      placeholder="First name"
                      value={nlName}
                      onChange={(e) => setNlName(e.target.value)}
                      className="about-newsletter-input"
                      data-testid="about-newsletter-name"
                    />
                    <input
                      type="email"
                      placeholder="Your email"
                      value={nlEmail}
                      onChange={(e) => setNlEmail(e.target.value)}
                      required
                      className="about-newsletter-input"
                      data-testid="about-newsletter-email"
                    />
                    <button
                      type="submit"
                      className="btn btn-primary about-newsletter-btn"
                      disabled={nlLoading}
                      data-testid="about-newsletter-submit"
                    >
                      {nlLoading ? 'Joining\u2026' : 'Stay in touch'}
                    </button>
                  </form>
                )}
                {nlStatus === 'error' && (
                  <p className="about-newsletter-error" data-testid="about-newsletter-error">
                    Something went wrong. Please try again or email admin@bindingthread.com.au.
                  </p>
                )}
              </div>
            </section>
          </div>
        </div>
      </section>
    </div>
  );
}

export default AboutPage;
